import * as React from 'react';
import {emotionStyled, styled} from '@packages/utilities/styled';
import NumberInput from '@packages/systems/design-system/WebflowNumberInput';
import {FlexRow} from '@packages/systems/design-system/Layout';

import {
  checkType,
  emptyCheckerState,
  Expressions,
  getMeta,
  Result,
  TypeImplementation,
  Types,
  withMeta,
  type MetaAttributes,
} from '@packages/systems/wfdl';

import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {iconName, pluginName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  ENumber,
  TNumber,
  EEnum,
  TEnum,
  Expression,
} from '@packages/systems/wfdl-ast';
import {
  updateExpressionAction,
  ExpressionEditor,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {Text, HStack} from '@packages/systems/spring';
import {
  bindValue,
  clamp,
  isInteger,
} from '@packages/systems/core/utils/NumberUtils';

const DECIMAL_PREVIEW_VALUES = {
  0: 'Integer',
  1: '1.2',
  2: '1.23',
  3: '1.234',
  4: '1.2345',
  5: '1.23456',
  6: '1.234567',
  7: '1.2345678',
  8: '1.23456789',
} as const;

const decimalPreviewValue = (precision: number): string => {
  return (
    DECIMAL_PREVIEW_VALUES[precision as keyof typeof DECIMAL_PREVIEW_VALUES] ??
    ''
  );
};

type MetaConfiguration = {
  min?: number;
  max?: number;
  precision?: number;
};

function getConfiguration(meta: unknown): null | MetaConfiguration {
  if (
    !meta ||
    typeof meta !== 'object' ||
    'value' in meta === false ||
    meta.value === null ||
    typeof meta.value !== 'object'
  ) {
    return null;
  }
  const conf = meta.value;
  const min =
    'min' in conf && typeof conf.min === 'number' ? conf.min : undefined;
  const max =
    'max' in conf && typeof conf.max === 'number' ? conf.max : undefined;
  const precision =
    'precision' in conf && typeof conf.precision === 'number'
      ? conf.precision
      : undefined;
  return {
    min,
    max,
    precision,
  };
}

const systemDefault = {defaultValue: 0, label: 'Number'} as const;

// Stepping limited to the precision/decimals of the prop
// Props without decimals keep stepping by 1 (integers).
export const stepFromPrecision = (precision: number | undefined): number =>
  precision === undefined || precision <= 0 ? 1 : Math.pow(10, -precision);

const StatefulNumberInput = ({
  min,
  max,
  precision,
  value,
  dispatchNumber,
  transaction,
  automationId,
  placeholder,
}: {
  min: number;
  max: number;
  precision: number | undefined;
  value: number;
  dispatchNumber: (newExpr: Expression) => void;
  transaction: {begin: () => void; commit: () => void; abort: () => void};
  automationId: string;
  placeholder: string;
}) => {
  const [internalValue, setInternalValue] = React.useState<number>(value);
  const step = stepFromPrecision(precision);

  // Sync internal state when `value` prop changes
  React.useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Enforce min/max/precision constraints when they change
  React.useEffect(() => {
    const newValue = bindValue(internalValue, {
      format: isInteger(internalValue) ? 'integer' : 'decimal',
      minValue: min,
      maxValue: max,
      precision,
    });
    if (newValue !== internalValue) {
      dispatchNumber(ENumber(newValue));
      setInternalValue(newValue);
    }
  }, [min, max, precision, internalValue, dispatchNumber]);

  const onChange = React.useCallback(
    (numberValue: number) => {
      dispatchNumber(ENumber(numberValue));
      setInternalValue(numberValue);
    },
    [dispatchNumber, setInternalValue]
  );

  const onInputFocused = React.useCallback(() => {
    transaction.begin();
  }, [transaction]);

  const onInputBlurred = React.useCallback(() => {
    transaction.commit();
  }, [transaction]);

  return (
    <Input
      data-automation-id={automationId}
      onChange={onChange}
      value={internalValue}
      step={step}
      min={min}
      max={max}
      type="text"
      flexGrow={1}
      placeholder={placeholder}
      onInputFocused={onInputFocused}
      onInputBlurred={onInputBlurred}
      alwaysShowRightInputTicks
    />
  );
};

const NumberRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const type = getAnn(expr)?.type;
  const meta = type ? getMeta(type) : null;
  const conf = getConfiguration(meta);
  const min = conf?.min !== undefined ? conf.min : Number.NEGATIVE_INFINITY;
  const max = conf?.max !== undefined ? conf.max : Number.POSITIVE_INFINITY;
  const precision =
    conf?.precision !== undefined ? clamp(0, 8, conf.precision) : undefined;
  const number = ContextualExpressions.getNumber(stripAnn(expr));
  const rawValue = number !== null ? number : 0;
  const format = isInteger(rawValue) ? 'integer' : 'decimal';
  // Format the value, the min/max/precision constraints can
  // change between the value being set so this might be different
  // than the stored value.
  const value = bindValue(rawValue, {
    format,
    minValue: min,
    maxValue: max,
    precision,
  });

  const {
    env: {transaction},
  } = useExpressionEditorImplementationContext();

  const onChange = React.useMemo(
    () => (newExpr: Expression) => {
      const newNum = Expressions.getNumber(newExpr);
      if (newNum === null) {
        dispatch(updateExpressionAction(newExpr));
      } else {
        // Make sure to update the expression with the formatted value
        const newFormat = isInteger(newNum) ? 'integer' : 'decimal';
        const formattedNum = bindValue(newNum, {
          format: newFormat,
          minValue: min,
          maxValue: max,
          precision,
        });
        dispatch(updateExpressionAction(ENumber(formattedNum)));
      }
    },
    [dispatch, max, min, precision]
  );

  return (
    <FlexRow>
      <StatefulNumberInput
        min={min}
        max={max}
        precision={precision}
        value={value}
        dispatchNumber={onChange}
        transaction={transaction}
        automationId={
          type ? Types.generateAutomationIds(type) : 'number-primitive'
        }
        placeholder={meta?.placeholder || 'Enter a number...'}
      />
    </FlexRow>
  );
};

type NumberMetaState = {
  value: MetaConfiguration;
};

const NumberMetaUI = ({
  meta,
  onChange,
}: {
  meta: MetaAttributes | null;
  onChange: (arg1: MetaAttributes) => void;
}) => {
  const [state, setState] = React.useState<NumberMetaState>(() => {
    const conf = getConfiguration(meta);
    return {
      value: {
        min: conf?.min,
        max: conf?.max,
        precision: conf?.precision,
      },
    };
  });

  React.useEffect(() => {
    const newConf = getConfiguration(meta);
    setState({
      value: {
        min: newConf?.min,
        max: newConf?.max,
        precision: newConf?.precision,
      },
    });
  }, [meta]);

  const {
    env: {transaction},
  } = useExpressionEditorImplementationContext();

  const createChangeHandler = React.useCallback(
    (field: 'min' | 'max' | 'precision') => ({
      onChange: (value: number) => {
        const newState = {...state, value: {...state.value, [field]: value}};
        setState(newState);
        onChange(newState);
      },
      onReset: () => {
        const newState = {
          ...state,
          value: {...state.value, [field]: undefined},
        };
        setState(newState);
        onChange(newState);
      },
    }),
    [onChange, state]
  );

  const previewValue =
    state.value.precision != null
      ? decimalPreviewValue(state.value.precision)
      : null;
  const onInputFocused = React.useCallback(() => {
    transaction.begin();
  }, [transaction]);

  const onInputBlurred = React.useCallback(() => {
    transaction.commit();
  }, [transaction]);

  return (
    <>
      <Text colorVariant="secondary">Min</Text>
      <InputContainer>
        <NumberInput
          data-automation-id="number-min"
          placeholder="None"
          min={Number.NEGATIVE_INFINITY}
          max={state.value.max || Number.POSITIVE_INFINITY}
          step={1}
          alwaysShowRightInputTicks
          value={state.value.min}
          optional
          onInputFocused={onInputFocused}
          onInputBlurred={onInputBlurred}
          {...createChangeHandler('min')}
        />
      </InputContainer>
      <Text colorVariant="secondary">Max</Text>
      <InputContainer>
        <NumberInput
          data-automation-id="number-max"
          placeholder="None"
          min={state.value.min || Number.NEGATIVE_INFINITY}
          max={Number.POSITIVE_INFINITY}
          step={1}
          value={state.value.max}
          alwaysShowRightInputTicks
          optional
          onInputFocused={onInputFocused}
          onInputBlurred={onInputBlurred}
          {...createChangeHandler('max')}
        />
      </InputContainer>
      <Text colorVariant="secondary">Decimals</Text>
      <HStack>
        <InputContainer>
          <NumberInput
            data-automation-id="number-decimals"
            placeholder="Auto"
            min={0}
            max={8}
            step={1}
            precision={0}
            value={state.value.precision}
            alwaysShowRightInputTicks
            optional
            onInputFocused={onInputFocused}
            onInputBlurred={onInputBlurred}
            {...createChangeHandler('precision')}
          />
        </InputContainer>
        {previewValue ? (
          <PreviewContainer>
            <Text>{previewValue}</Text>
          </PreviewContainer>
        ) : null}
      </HStack>
    </>
  );
};

export const NumberPrimitiveType = TypeImplementation.Custom({
  parseValue: (_) =>
    Result.Error('Cannot use Literal expressions to create Number values.'),
  system: systemDefault,
  render: NumberRender,
  renderSummary: ({expr}: {expr: ContextualExpression}) => (
    <span>{ContextualExpressions.getNumber(stripAnn(expr))}</span>
  ),
  metaUI: NumberMetaUI,
  icon: TypeImplementation.IconByName(
    qualify(pluginName('Basic'), iconName('Number'))
  ),
  formatValue: (meta: unknown, value: unknown) => {
    const conf = getConfiguration(meta);
    if (!conf) {
      return value;
    }
    const valueNumber = Number(value);
    const parsedValue = Number.isNaN(valueNumber) ? 0 : valueNumber;
    const precision =
      conf.precision === undefined || conf.precision < 0
        ? undefined
        : clamp(0, 8, conf.precision);
    const format = isInteger(parsedValue) ? 'integer' : 'decimal';
    return bindValue(parsedValue, {
      format,
      minValue: conf.min,
      maxValue: conf.max,
      precision,
    });
  },
});

// @ts-expect-error - TS2769 - No overload matches this call.
const Input = styled(NumberInput)({
  width: '100%',
});

const enumToNumberMap: {[key: string]: number} = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  any: -1,
};
const numberToEnumMap: {[key: string]: string} = {
  0: 'zero',
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  '-1': 'any',
};
const decimalsAsEnumType = withMeta(
  {variant: 'menu', withoutLivePreview: true},
  TEnum({
    zero: '1 (to integer)',
    one: '1.2 (to 1 decimal)',
    two: '1.23 (to 2 decimals)',
    three: '1.234 (to 3 decimals)',
    four: '1.2345 (to 4 decimals)',
    five: '1.23456 (to 5 decimals)',
    six: '1.234567 (to 6 decimals)',
    seven: '1.2345678 (to 7 decimals)',
    eight: '1.23456789 (to 8 decimals)',
    any: 'any format',
  })
);
const DecimalNumberSelectorTypeRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const numValue = ContextualExpressions.getNumber(stripAnn(expr));

  // If the expression is not a number, show the default expression editor
  // as this is an unexpected state.
  if (numValue === null) {
    return <ExpressionEditor dispatch={dispatch} expression={expr} />;
  }

  const clampedNum = clamp(-1, 8, numValue);
  const valueAsEnum = numberToEnumMap[clampedNum];
  if (!valueAsEnum) {
    return null;
  }

  // The enum value is not bindable, we do not need
  // the pre-existing page tree or plugins as state
  const [, newContextualExpression] = checkType(
    EEnum(valueAsEnum),
    decimalsAsEnumType
  )(emptyCheckerState);

  const enumChangeHandler: TypeImplementation.ExpressionEditorDispatch = (
    eeAction
  ) => {
    if (eeAction.type === 'EXPRESSION_ACTION') {
      const newExpression = eeAction.payload.newExpression;
      const enumValue = Expressions.getEnum(newExpression);
      if (!enumValue) {
        return;
      }
      const newNumberValue = enumToNumberMap[enumValue];
      if (newNumberValue === undefined) {
        return;
      }
      dispatch(updateExpressionAction(ENumber(newNumberValue)));
    }
  };
  return (
    <ExpressionEditor
      dispatch={enumChangeHandler}
      expression={newContextualExpression}
    />
  );
};
export const DecimalNumberSelectorType = TypeImplementation.Alias({
  type: TNumber,
  render: DecimalNumberSelectorTypeRender,
});

const PreviewContainer = emotionStyled('div')({
  border: '$border1',
  borderRadius: '$2',
  paddingTop: '$1',
  paddingBottom: '$1',
  paddingRight: '$2',
  paddingLeft: '$2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '80.5px',
});

const InputContainer = emotionStyled('div')({
  width: '80.5px',
});
